import * as React from 'react';
import { connect } from 'react-redux';
import classnames from 'classnames';
import { findIndex as _findIndex } from 'lodash';
import moment from 'moment';

import Button, { EditButton } from '@components/Button';
import Input from '@components/Input';
import ModalBase from '@components/ModalBase';
import Table from '@components/Table';
import Toggle from '@components/Toggle';

import { fetchViolationTypes } from '@actions/violationType';

import { getEmptyErrorState } from '@utils/AppUtil';
import CyberdyneValidator from '@utils/cyberdyneValidator';
import ErrorMessageGenerator from '@utils/ErrorMessageGenerator';

import ExecutiveReportModal from './ExecutiveReportModal';

interface IProductCEFormProps {
  show: boolean;
  xerce: IXerceResponse;
  onClose: (e: React.SyntheticEvent<HTMLElement>) => void;
  onSubmit: (e: React.SyntheticEvent<HTMLElement>, xerceRequest: IXerceResponse) => void;
  // from redux
  fetchViolationTypes: TFetchViolationTypes;
  violationTypes: ISystemXerceViolationTypeResponse[];
  onDisableFineTracking: (allowFineTracking: boolean) => void;
}

interface IProductCEFormState {
  xerce: IXerceResponse;
  isEdit: boolean;
  selectedExecutiveReport: IConfigExecutiveReportResponse;
  isExecutiveReportModalOpen: boolean;
  error: {
    name: IFieldValidation;
    fineReceiptNumberPrefix: IFieldValidation;
    fineReceiptNumberInitialization: IFieldValidation;
    caseNumberPrefix: IFieldValidation;
    caseNumberInitialization: IFieldValidation;
    collectionReport: IFieldValidation;
  };
}

const errorMsgGenerator = new ErrorMessageGenerator();

class ProductCEForm extends React.Component<IProductCEFormProps, IProductCEFormState> {
  readonly productName = 'Code Enforcement';
  readonly validator = new CyberdyneValidator();

  constructor(props: IProductCEFormProps) {
    super(props);

    this.state = this.getEmptyState();
  }

  componentDidMount() {
    this.props.fetchViolationTypes();
  }

  componentDidUpdate(prevProps: IProductCEFormProps) {
    const hasOpened = prevProps.show !== this.props.show && this.props.show;
    const isEdit = !!this.props.xerce;
    const areViolationTypesAvailable = this.props.violationTypes;

    if (hasOpened && isEdit) {
      this.setState({
        xerce: this.getXerceFromProps(this.props),
        isEdit: true,
      });
    }

    if (hasOpened && !isEdit && areViolationTypesAvailable) {
      this.setState({
        xerce: {
          ...this.getEmptyState().xerce,
          violationTypes: this.getViolationTypeOptionsFromProps(this.props),
        },
        isEdit: false,
      });
    }
  }

  getEmptyState = (): IProductCEFormState => {
    const errorInitState = {
      errorState: null,
      errorMessage: '',
    };

    return {
      xerce: {
        name: '',
        isActive: true,
        caseNumberPrefix: '',
        caseNumberInitialization: 1,
        isCaseNumberResettable: true,
        fineReceiptNumberPrefix: '',
        isFineReceiptNumberResettable: true,
        isFineReceiptNumberYearIncluded: true,
        isCaseNumberYearIncluded: true,
        fineReceiptNumberInitialization: 1,
        violationTypes: [],
        allowFineTracking: false,
        collectionStatus: false,
        isStandardCollectionActive: true,
        collectionReportTypes: this.getCollectionTypes(),
        isMultiUnitPropertyEnabled: false,
      },
      isExecutiveReportModalOpen: false,
      selectedExecutiveReport: null,
      error: {
        name: Object.assign({}, errorInitState),
        fineReceiptNumberPrefix: Object.assign({}, errorInitState),
        fineReceiptNumberInitialization: Object.assign({}, errorInitState),
        caseNumberPrefix: Object.assign({}, errorInitState),
        caseNumberInitialization: Object.assign({}, errorInitState),
        collectionReport: getEmptyErrorState(),
      },
      isEdit: false,
    };
  };

  getCollectionTypes = () =>
    this.props.xerce?.violationTypes
      ?.filter((vt) => vt.isActive)
      ?.map((violationType) => ({
        id: violationType.id,
        label: violationType.label,
        systemXerceViolationTypeId: violationType.systemXerceViolationTypeId,
        isPaymentCollectionActive: false,
      }));

  getXerceFromProps = (nextProps: IProductCEFormProps): IXerceResponse => ({
    ...nextProps.xerce,
    caseNumberPrefix: nextProps.xerce.caseNumberPrefix ? nextProps.xerce.caseNumberPrefix : '',
    fineReceiptNumberPrefix: nextProps.xerce.fineReceiptNumberPrefix ? nextProps.xerce.fineReceiptNumberPrefix : '',
    isResolutionHistoryTracked: nextProps.xerce.isResolutionHistoryTracked,
    isMultiUnitPropertyEnabled: nextProps.xerce.isMultiUnitPropertyEnabled ?? false,
  });

  getViolationTypeOptionsFromProps = (nextProps: IProductCEFormProps): IConfigXerceViolationTypeResponse[] =>
    nextProps.violationTypes.map((violationType) => ({
      systemXerceViolationTypeId: violationType.id,
      isActive: false,
      label: violationType.label,
      id: null,
    }));

  validateForm = () => {
    let isValid = true;
    const { xerce } = this.state;
    const error = this.getEmptyState().error;

    if (!xerce.name) {
      error.name.errorState = IValidationState.ERROR;
      error.name.errorMessage = 'Product name cannot be empty';

      isValid = false;
    } else if (xerce.name.length > 20) {
      error.name.errorState = IValidationState.ERROR;
      error.name.errorMessage = 'Product name cannot be longer than 20 characters';

      isValid = false;
    } else if (!this.validator.isAlphaNumericWithSpaces(xerce.name)) {
      error.name.errorState = IValidationState.ERROR;
      error.name.errorMessage = 'Enter valid product name';

      isValid = false;
    }

    if (!xerce.caseNumberInitialization) {
      error.caseNumberInitialization.errorState = IValidationState.ERROR;
      error.caseNumberInitialization.errorMessage = 'Number Initialization is a mandatory field';

      isValid = false;
    } else if (xerce.caseNumberInitialization < 0 || xerce.caseNumberInitialization > 99999999) {
      error.caseNumberInitialization.errorState = IValidationState.ERROR;
      error.caseNumberInitialization.errorMessage = 'Number Initialization can be atmost 8 characters long';

      isValid = false;
    }

    if (!xerce.fineReceiptNumberInitialization) {
      error.fineReceiptNumberInitialization.errorState = IValidationState.ERROR;
      error.fineReceiptNumberInitialization.errorMessage = 'Number Initialization is a mandatory field';

      isValid = false;
    } else if (xerce.fineReceiptNumberInitialization < 0 || xerce.fineReceiptNumberInitialization > 99999999) {
      error.fineReceiptNumberInitialization.errorState = IValidationState.ERROR;
      error.fineReceiptNumberInitialization.errorMessage = 'Number Initialization can be atmost 8 characters long';

      isValid = false;
    }

    if (xerce.caseNumberPrefix && !this.validator.isValidNumberingPrefix(xerce.caseNumberPrefix)) {
      error.caseNumberPrefix.errorState = IValidationState.ERROR;
      error.caseNumberPrefix.errorMessage = 'Invalid Number Prefix';

      isValid = false;
    } else if (xerce.caseNumberPrefix.trim().length > 5) {
      error.caseNumberPrefix.errorState = IValidationState.ERROR;
      const errMsg = errorMsgGenerator.getCharacterLimitMessage('Case Number Prefix', 5);
      error.caseNumberPrefix.errorMessage = errMsg;

      isValid = false;
    }

    if (xerce.fineReceiptNumberPrefix && !this.validator.isValidNumberingPrefix(xerce.fineReceiptNumberPrefix)) {
      error.fineReceiptNumberPrefix.errorState = IValidationState.ERROR;
      error.fineReceiptNumberPrefix.errorMessage = 'Invalid Number Prefix';

      isValid = false;
    } else if (xerce.fineReceiptNumberPrefix.trim().length > 5) {
      error.fineReceiptNumberPrefix.errorState = IValidationState.ERROR;
      const errMsg = errorMsgGenerator.getCharacterLimitMessage('Fine Number Prefix', 5);
      error.fineReceiptNumberPrefix.errorMessage = errMsg;

      isValid = false;
    }

    if (xerce.collectionStatus && !this.isAnyCollectionReportActive()) {
      error.collectionReport.errorState = IValidationState.ERROR;
      error.collectionReport.errorMessage = 'At least one Collections report must be active.';

      isValid = false;
    }

    this.setState({ error });

    return isValid;
  };

  onClose = (e: React.SyntheticEvent<HTMLElement>) => {
    this.setState(this.getEmptyState(), () => this.props.onClose(e));
  };

  onSubmit = (e: React.SyntheticEvent<HTMLElement>) => {
    if (this.validateForm()) {
      const xerce = this.state.xerce;
      this.setState(this.getEmptyState(), () => this.props.onSubmit(e, { ...xerce, name: xerce.name.trim() }));
    }
  };

  twoChoiceButtons = (
    options: Array<{ value: any; label: string }>,
    currentValue: any,
    handleChange: (newValue: any) => void,
    disabled?: boolean
  ) => (
    <div className="multi-choice-buttons">
      {options &&
        options.map((option, index) => (
          <Button
            key={index}
            onClick={(e) => handleChange(option.value)}
            bsStyle={currentValue === option.value ? ButtonStyle.PRIMARY : ButtonStyle.DEFAULT}
            content={option.label}
            disabled={disabled}
          />
        ))}
    </div>
  );

  handleChange = (event: React.SyntheticEvent<HTMLInputElement>) => {
    const name = event.currentTarget.name;
    const inputType = event.currentTarget.type;
    const value = inputType === 'number' ? parseInt(event.currentTarget.value, 10) : event.currentTarget.value;

    this.setState((prevState) => ({
      xerce: {
        ...prevState.xerce,
        [name]: value,
      },
    }));
  };

  handleFineTrackingChange = (event: React.SyntheticEvent<HTMLInputElement>) => {
    const allowFineTracking = event.currentTarget.checked;
    if (!allowFineTracking && this.props.onDisableFineTracking) {
      this.props.onDisableFineTracking(allowFineTracking);
    }

    this.setState((prevState) => ({
      xerce: {
        ...prevState.xerce,
        allowFineTracking,
      },
    }));
  };

  handleCollectionStatusChange = (event: React.SyntheticEvent<HTMLInputElement>) => {
    const collectionStatus = event.currentTarget.checked;

    this.setState((prevState) => ({
      xerce: {
        ...prevState.xerce,
        collectionStatus,
      },
    }));
  };

  handleResolutionHistoryChange = (event: React.SyntheticEvent<HTMLInputElement>) => {
    const resolutionHistoryStatus = event.currentTarget.checked;

    this.setState((prevState) => ({
      xerce: {
        ...prevState.xerce,
        isResolutionHistoryTracked: resolutionHistoryStatus,
      },
    }));
  };

  handleMultiUnitPropertyChange = (event: React.SyntheticEvent<HTMLInputElement>) => {
    const multiUnitPropertyStatus = event.currentTarget.checked;

    this.setState((prevState) => ({
      xerce: {
        ...prevState.xerce,
        isMultiUnitPropertyEnabled: multiUnitPropertyStatus,
      },
    }));
  };

  handleTwoButtonsChange = (name: string, value: any) => {
    this.setState((prevState) => ({
      xerce: {
        ...prevState.xerce,
        [name]: value,
      },
    }));
  };

  handleViolationTypeToggle = (event: React.SyntheticEvent<HTMLInputElement>, violationTypeIndex: number) => {
    const value = event.currentTarget.checked;

    this.setState((prevState) => ({
      xerce: {
        ...prevState.xerce,
        violationTypes: [
          ...prevState.xerce.violationTypes.slice(0, violationTypeIndex),
          {
            ...prevState.xerce.violationTypes[violationTypeIndex],
            isActive: value,
          },
          ...prevState.xerce.violationTypes.slice(violationTypeIndex + 1),
        ],
      },
    }));
  };

  handleStandardCollectionToggle = (event: React.SyntheticEvent<HTMLInputElement>) => {
    const value = event.currentTarget.checked;

    this.setState((prevState) => ({
      ...prevState,
      xerce: {
        ...prevState.xerce,
        isStandardCollectionActive: value,
      },
    }));
  };

  handleCollectionReportToggle = (event: React.SyntheticEvent<HTMLInputElement>, collectionReportTypeIndex: number) => {
    const value = event.currentTarget.checked;

    this.setState((prevState) => ({
      ...prevState,
      xerce: {
        ...prevState.xerce,
        collectionReportTypes: [
          ...prevState.xerce.collectionReportTypes.slice(0, collectionReportTypeIndex),
          {
            ...prevState.xerce.collectionReportTypes[collectionReportTypeIndex],
            isPaymentCollectionActive: value,
          },
          ...prevState.xerce.collectionReportTypes.slice(collectionReportTypeIndex + 1),
        ],
      },
    }));
  };

  isAnyCollectionReportActive = () => {
    const { xerce } = this.state;
    const activeCollectionReportType = xerce?.collectionReportTypes?.filter(
      (collectionReportType) => collectionReportType.isPaymentCollectionActive
    );

    if (xerce?.isStandardCollectionActive) {
      return true;
    } else {
      return activeCollectionReportType.length > 0;
    }
  };

  renderTitle = () =>
    this.state.isEdit ? `Edit & Configure ${this.productName}` : `Add & Configure ${this.productName}`;

  renderProductInfo = () => {
    const statusOptions = [
      { value: true, label: 'Active' },
      { value: false, label: 'Inactive' },
    ];
    const { xerce, error } = this.state;

    return (
      <div className="product-form__info">
        <Input
          field={{
            label: 'Product Name',
            type: CustomFieldType.TEXT,
            isRequired: true,
            value: xerce.name,
          }}
          error={error.name}
          width={12}
          name="name"
          onChange={this.handleChange}
        />
        <Input
          width={12}
          field={{
            label: 'Status',
            type: null,
            value: this.state.xerce.isActive,
            isRequired: true,
          }}
          renderField={(name, onChange) =>
            this.twoChoiceButtons(statusOptions, xerce.isActive, (value) => this.handleTwoButtonsChange(name, value))
          }
          name="isActive"
          onChange={() => null}
        />
      </div>
    );
  };

  renderViolationTypes = () => {
    const { xerce } = this.state;
    const { violationTypes } = xerce;

    return (
      <div className="product-form__violation-types">
        {violationTypes.map((violationType, index) => (
          <Toggle
            key={violationType.systemXerceViolationTypeId}
            bsStyle="primary"
            onToggle={(event) => this.handleViolationTypeToggle(event, index)}
            toggleState={violationType.isActive}
          >
            {violationType.label}
          </Toggle>
        ))}
      </div>
    );
  };

  renderProductModules = () => (
    <div className="product-form__product-modules">
      <Toggle bsStyle="primary" onToggle={() => null} toggleState={true} disabled={true}>
        CDBG
      </Toggle>
      <Toggle
        bsStyle="primary"
        onToggle={this.handleFineTrackingChange}
        toggleState={this.state.xerce.allowFineTracking}
        disabled={false}
      >
        Fines Tracking
      </Toggle>
      <Toggle bsStyle="primary" onToggle={() => null} toggleState={true} disabled={true}>
        Time Tracking
      </Toggle>
      <Toggle
        bsStyle="primary"
        onToggle={this.handleCollectionStatusChange}
        toggleState={this.state.xerce.collectionStatus}
        disabled={false}
      >
        Collections
      </Toggle>
      <Toggle
        bsStyle="primary"
        onToggle={this.handleResolutionHistoryChange}
        toggleState={this.state.xerce.isResolutionHistoryTracked}
        disabled={false}
      >
        Resolution History
      </Toggle>
      <Toggle
        bsStyle="primary"
        onToggle={this.handleMultiUnitPropertyChange}
        toggleState={this.state.xerce.isMultiUnitPropertyEnabled}
        disabled={false}
      >
        Multi-Unit Properties
      </Toggle>
    </div>
  );

  renderCollectionReports = () => {
    const { errorState, errorMessage } = this.state.error.collectionReport;
    const { xerce } = this.state;

    return (
      <div>
        {xerce?.collectionStatus && <h2>Collections Report Types</h2>}
        {xerce?.collectionStatus && (
          <div className="collection-report-types">
            <Toggle
              bsStyle="primary"
              onToggle={this.handleStandardCollectionToggle}
              toggleState={xerce.isStandardCollectionActive}
              inverted={false}
            >
              Standard Collections
            </Toggle>
            {xerce.collectionReportTypes?.map((collectionReportType, index) => (
              <Toggle
                key={collectionReportType.id}
                bsStyle="primary"
                onToggle={(e) => this.handleCollectionReportToggle(e, index)}
                toggleState={collectionReportType.isPaymentCollectionActive}
                inverted={false}
              >
                {collectionReportType.label} Collections
              </Toggle>
            ))}
          </div>
        )}
        {errorState === IValidationState.ERROR && (
          <span className="collection-report-field__error">{errorMessage}</span>
        )}
      </div>
    );
  };

  renderCaseNumbering = () => {
    const twoStateOptions = [
      { value: true, label: 'Yes' },
      { value: false, label: 'No' },
    ];
    const { xerce, error } = this.state;
    const { isCaseNumberYearIncluded, caseNumberPrefix, isCaseNumberResettable, caseNumberInitialization } = xerce;
    const caseNumberDisplayYear = isCaseNumberYearIncluded ? `${moment().format('YY')}-` : '';
    const caseNumberDisplayPrefix = caseNumberPrefix ? `${caseNumberPrefix}-` : '';
    const caseNumberDisplayInitialization = caseNumberInitialization ? `${caseNumberInitialization}` : '';
    const caseNumberDisplay =
      `${caseNumberDisplayPrefix}` + `${caseNumberDisplayYear}` + `${caseNumberDisplayInitialization}`;
    const disclaimerText = `Case Number Initialization
      will be reset on the Mid-Night of every New Year.`;

    return (
      <div className="product-form__case-numbering">
        <div className="product-form__case-numbering-settings">
          <div className="product-form__case-numbering-settings-fields">
            <Input
              field={{
                label: 'Prefix',
                type: CustomFieldType.TEXT,
                isRequired: false,
                value: caseNumberPrefix,
              }}
              error={error.caseNumberPrefix}
              width={12}
              name="caseNumberPrefix"
              onChange={this.handleChange}
            />
            <Input
              width={12}
              field={{
                label: 'Include Year?',
                type: null,
                value: isCaseNumberYearIncluded,
                isRequired: true,
              }}
              renderField={(name, onChange) =>
                this.twoChoiceButtons(twoStateOptions, xerce.isCaseNumberYearIncluded, (value) =>
                  this.handleTwoButtonsChange(name, value)
                )
              }
              name="isCaseNumberYearIncluded"
              onChange={() => null}
            />
            <Input
              field={{
                label: 'Number Initialization',
                type: CustomFieldType.NUMBER,
                isRequired: true,
                value: caseNumberInitialization,
              }}
              error={error.caseNumberInitialization}
              width={12}
              name="caseNumberInitialization"
              onChange={this.handleChange}
            />
            {isCaseNumberYearIncluded && (
              <Input
                width={12}
                field={{
                  label: 'Reset Numbers Each Year?',
                  type: null,
                  value: isCaseNumberResettable,
                  isRequired: false,
                }}
                renderField={(name, onChange) =>
                  this.twoChoiceButtons(twoStateOptions, isCaseNumberResettable, (value) =>
                    this.handleTwoButtonsChange(name, value)
                  )
                }
                name="isCaseNumberResettable"
                onChange={() => null}
              />
            )}
          </div>
          {isCaseNumberYearIncluded && (
            <div className="product-form__disclaimer">
              <i />
              <label>{disclaimerText}</label>
            </div>
          )}
        </div>
        <div className="product-form__case-numbering-display">
          <div className="product-form__secondary-header">Case Number Display:</div>
          <label>{caseNumberDisplay}</label>
        </div>
      </div>
    );
  };

  renderFineReceiptNumbering = () => {
    const twoStateOptions = [
      { value: true, label: 'Yes' },
      { value: false, label: 'No' },
    ];
    const { xerce, error, isEdit } = this.state;
    const {
      isFineReceiptNumberYearIncluded,
      fineReceiptNumberPrefix,
      isFineReceiptNumberResettable,
      fineReceiptNumberInitialization,
    } = xerce;
    const fineReceiptNumberDisplayYear = isFineReceiptNumberYearIncluded ? `${moment().format('YY')}-` : '';
    const fineReceiptNumberDisplayPrefix = fineReceiptNumberPrefix ? `${fineReceiptNumberPrefix}-` : '';
    // tslint:disable-next-line:max-line-length
    const fineReceiptNumberDisplayInitialization = fineReceiptNumberInitialization
      ? `${fineReceiptNumberInitialization}`
      : '';
    const fineReceiptNumberDisplay =
      `${fineReceiptNumberDisplayPrefix}` +
      `${fineReceiptNumberDisplayYear}` +
      `${fineReceiptNumberDisplayInitialization}`;
    const disclaimerText = `Fine Receipt Number Initialization
      will be reset on the Mid-Night of every New Year.`;

    return (
      <div className="product-form__fine-receipt-numbering">
        <div className="product-form__fine-receipt-numbering-settings">
          <div className="product-form__fine-receipt-numbering-settings-fields">
            <Input
              field={{
                label: 'Prefix',
                type: CustomFieldType.TEXT,
                isRequired: false,
                value: fineReceiptNumberPrefix,
              }}
              disabled={isEdit}
              error={error.fineReceiptNumberPrefix}
              width={12}
              name="fineReceiptNumberPrefix"
              onChange={this.handleChange}
            />
            <Input
              width={12}
              field={{
                label: 'Include Year?',
                type: null,
                value: isFineReceiptNumberYearIncluded,
                isRequired: true,
              }}
              renderField={(name, onChange) =>
                this.twoChoiceButtons(
                  twoStateOptions,
                  isFineReceiptNumberYearIncluded,
                  (value) => this.handleTwoButtonsChange(name, value),
                  isEdit
                )
              }
              name="isFineReceiptNumberYearIncluded"
              onChange={() => null}
            />
            <Input
              field={{
                label: 'Number Initialization',
                type: CustomFieldType.NUMBER,
                isRequired: true,
                value: fineReceiptNumberInitialization,
              }}
              disabled={isEdit}
              error={error.fineReceiptNumberInitialization}
              width={12}
              name="fineReceiptNumberInitialization"
              onChange={this.handleChange}
            />
            {isFineReceiptNumberYearIncluded && (
              <Input
                width={12}
                field={{
                  label: 'Reset Numbers Each Year?',
                  type: null,
                  value: isFineReceiptNumberResettable,
                  isRequired: false,
                }}
                renderField={(name, onChange) =>
                  this.twoChoiceButtons(
                    twoStateOptions,
                    isFineReceiptNumberResettable,
                    (value) => this.handleTwoButtonsChange(name, value),
                    isEdit
                  )
                }
                name="isFineReceiptNumberResettable"
                onChange={() => null}
              />
            )}
          </div>
          {isFineReceiptNumberYearIncluded && (
            <div className="product-form__disclaimer">
              <i />
              <label>{disclaimerText}</label>
            </div>
          )}
        </div>
        <div className="product-form__fine-receipt-numbering-display">
          <div className="product-form__secondary-header">Fine Receipt Number Display:</div>
          <label>{fineReceiptNumberDisplay}</label>
        </div>
      </div>
    );
  };

  renderExecutiveReportsSettings = () => {
    const columns = [
      { accessor: 'label', Header: 'Dashboard name', sortable: false },
      { accessor: 'dashboardId', Header: 'Dashboard ID', sortable: false },
      {
        accessor: 'isActive',
        Header: 'Status',
        sortable: false,
        Cell: (cell) => (cell.value ? 'Active' : 'Inactive'),
      },
      {
        accessor: '',
        Header: '',
        Cell: (cell) => (
          <EditButton
            onClick={this.toggleExecutiveReportSetting(cell.original)}
            className={classnames('space-right')}
          />
        ),
        sortable: false,
        width: 100,
      },
    ];
    const { executiveReports: rows } = this.state.xerce;

    return (
      <div className="product-form__executive-reports">
        <div>
          <Table noDataText={'No Dashboard Configured'} columns={columns} rows={rows} plainTable={true} />
        </div>
        {this.renderNewExecutiveReportActionButton()}
        <ExecutiveReportModal
          show={this.state.isExecutiveReportModalOpen}
          executiveReport={this.state.selectedExecutiveReport}
          allReports={rows}
          onClose={this.toggleExecutiveReportSetting(null)}
          onSubmit={this.onSaveExecutiveReport}
        />
      </div>
    );
  };

  onSaveExecutiveReport = (executiveReport: IConfigExecutiveReportRequest) => {
    this.toggleExecutiveReportSetting(null)(null);
    const allReports = [...this.state.xerce.executiveReports];
    if (executiveReport.id) {
      const editReportIdx = _findIndex(allReports, { id: executiveReport.id });
      allReports.splice(editReportIdx, 1, executiveReport);
    } else if (executiveReport.tempId) {
      const editReportIdx = _findIndex(allReports, {
        tempId: executiveReport.tempId,
      });
      if (editReportIdx > -1) {
        allReports.splice(editReportIdx, 1, executiveReport);
      } else {
        allReports.push(executiveReport);
      }
    }
    this.setState((prevState) => ({
      xerce: {
        ...prevState.xerce,
        executiveReports: [...allReports],
      },
    }));
  };

  toggleExecutiveReportSetting =
    (executiveReport: IConfigExecutiveReportResponse = null) =>
    (e: React.SyntheticEvent<HTMLElement>) => {
      const selectedExecutiveReport = executiveReport || null;

      this.setState((prevState) => ({
        isExecutiveReportModalOpen: !prevState.isExecutiveReportModalOpen,
        selectedExecutiveReport,
      }));
    };

  renderNewExecutiveReportActionButton = () => (
    <div onClick={this.toggleExecutiveReportSetting(null)} className="product-form__add-report">
      <i />
      Add Report
    </div>
  );

  renderActivationSettings = () => (
    <div className="product-form__activation">
      <div className="product-form__primary-header">Activation Settings</div>
      <div className="product-form__secondary-header">Violation Types</div>
      {this.renderViolationTypes()}
      <div className="product-form__secondary-header">Product Modules</div>
      {this.renderProductModules()}
      {this.state.isEdit && this.renderCollectionReports()}
    </div>
  );

  renderBody = () => (
    <div className="product-form__body">
      {this.renderProductInfo()}
      {this.renderActivationSettings()}
      <div className="product-form__primary-header">Case Numbering</div>
      {this.renderCaseNumbering()}
      <div className="product-form__primary-header">Fine Receipt Numbering</div>
      {this.renderFineReceiptNumbering()}
      {this.state.xerce.id && this.state.isEdit && (
        <>
          <div className="product-form__primary-header">QS Reports</div>
          {this.renderExecutiveReportsSettings()}
        </>
      )}
    </div>
  );

  renderFooter = () => {
    const submitBtnTxt = this.state.isEdit ? 'Save' : `Add ${this.productName}`;

    return (
      <div className="product-form__footer">
        <Button bsStyle={ButtonStyle.DEFAULT} onClick={this.onClose} content="Cancel" />
        <Button bsStyle={ButtonStyle.PRIMARY} onClick={this.onSubmit} content={submitBtnTxt} />
      </div>
    );
  };

  render() {
    return (
      <ModalBase
        show={this.props.show}
        onClose={this.onClose}
        titleElement={this.renderTitle()}
        bodyElement={this.renderBody()}
        footerElement={this.renderFooter()}
        className="product-form"
      />
    );
  }
}

const mapStateToProps = (state: IReduxStore) => ({
  violationTypes: state.violationType.systemViolationTypes,
});

const mapDispatchToProps = (dispatch: any) => ({
  fetchViolationTypes: () => dispatch(fetchViolationTypes()),
});

export default connect(mapStateToProps, mapDispatchToProps)(ProductCEForm);
